import React, { RefObject, useEffect, useMemo, useState } from 'react';
import { MomentInput } from 'moment';
import i18n from 'i18n-js';

import { formatOEENumber, localTime } from '@helpers';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';

import { Events, Event, TriggerShowPopup, TriggerHidePopup } from '@interfaces/timeline';
import Image from '@components/common/Image';

import { LegendRules } from '@components/machines/components/timeline/calculations/index';

import { machineStatusColorName } from '@components/wallboard/components/functions';
import { Popup } from '@components/machines/components/timeline';
import { TimelineBlockTitle } from './TimelineBlockTitle';
import {
  MIN_DISPLAY_BREAKPOINT,
  PRODUCTIVITY_STATUS_CONFIG,
  TIMELINE_CONFIG,
  TIMELINE_HAIRLINE_COLOR,
  TIMELINE_LINE_CONFIG,
  TIMELINE_UNDERLINE_TEXT_CONFIG,
} from '../constants';
import TimelineShimmer from './TimelineShimmer';
import Svg from '@styles/icons/svg';
import { scale } from '@components/frame';
import { MachinesScreen } from '@v2/pages/machines/common/enums';
import { useNavigation, useRoute } from '@react-navigation/native';
import { getTimelineFontStyles, computeEventsHash, optimizeEventsForRendering } from '@components/machines/components/timeline/helpers';
import { ColorFunction } from '@styles/colors/types';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { getNCFileByName } from '@reducers/manufacture';
import { generateImageURL } from '@reducers/blobStorage';
import { useSelector } from 'react-redux';
import { selectSelectedItem } from '@reducers/machines';
import { PERFORMANCE_MAIN_SCREEN } from '@v2/pages/performance/navigation';
import { useHandleBottomSheetActions } from '@v2/pages/common/hooks/useHandleBottomSheetActions';

const RenderNCFileImage = ({ item, C }: { item: Event; C: ColorFunction }) => {
  const dispatch = useAppDispatch();
  const [imageLink, setImageLink] = useState(null);

  useEffect(() => {
    (async () => {
      if (!item.ncFileMain) return;
      const ncFile = await dispatch(getNCFileByName(item.ncFileMain, ['ImageHash', 'ImageData']));
      if (ncFile?.searchResult?.[0]) {
        const file = ncFile.searchResult[0];
        const { imageHash, imageData } = file;
        if (imageHash) {
          const imageLink = await dispatch(
            generateImageURL({
              size: '_1x',
              cropped: imageData?.isCropped,
              item: { imageHash, extensionDerivative: imageData?.extensionDerivative },
              page: imageData?.pageNumber,
            })
          );
          setImageLink(imageLink);
        }
      }
    })();
  }, [item.ncFileMain]);

  const mainContainerStyles = {
    width: PRODUCTIVITY_STATUS_CONFIG.ncFileImageContainerWidth,
    height: PRODUCTIVITY_STATUS_CONFIG.ncFileImageContainerHeight,
  };

  if (!imageLink) {
    return <div style={{ ...mainContainerStyles, backgroundColor: C('Overlay/Light/Dim light') }} />;
  }

  return (
    <div
      style={{
        ...mainContainerStyles,
      }}
    >
      <Image
        {...{
          shadowColor: 'transparent',
          isShadow: Boolean(imageLink),
          isOverlay: true,
          borderRadius: 0,
          resizeMode: 'contain',
          style: {
            width: '100%',
            height: '100%',
            resizeMode: 'contain',
          },
          source: { uri: imageLink },
          alt: 'img',
          C,
        }}
      />
    </div>
  );
};

export default (props: {
  C: ColorFunction;
  events: Events;
  blockWidth: number;
  rules: LegendRules;
  triggerShowPopup: TriggerShowPopup;
  triggerHidePopup: TriggerHidePopup;
  timelineRef: RefObject<HTMLDivElement>;
  startTime: MomentInput;
  netUtilization?: number;
  showEvents?: boolean;
  onShowEventsPress?: () => void;
  title?: string;
  machineId: number;
}) => {
  const {
    C,
    events,
    blockWidth,
    rules,
    triggerShowPopup,
    triggerHidePopup,
    timelineRef,
    startTime,
    netUtilization,
    onShowEventsPress,
    showEvents,
    machineId,
    title = i18n.t('Productivity Status'),
  } = props;

  const { openScreenInsideBottomSheet } = useHandleBottomSheetActions();

  const selectedItem = useSelector(selectSelectedItem);

  const route = useRoute();
  const navigation = useNavigation<any>();

  const eventsHash = computeEventsHash(events?.events || []);

  const optimizedEvents = useMemo(() => {
    return optimizeEventsForRendering(events.events, startTime, rules.secondWidth);
  }, [events?.events?.length, eventsHash, startTime]);

  const onNetUtilizationPress = () => {
    const navigationAction = () =>
      navigation?.navigate(PERFORMANCE_MAIN_SCREEN, {
        screen: PERFORMANCE_MAIN_SCREEN + MachinesScreen.UTILIZATION,
        ...route.params,
      });
    openScreenInsideBottomSheet({ navigationAction, bottomSheetId: PERFORMANCE_MAIN_SCREEN });
  };

  const { fontStylesWeb } = getTimelineFontStyles();
  const buttonContentColor = 'Link/Light/Primary';

  return (
    <div style={{ marginBottom: TIMELINE_CONFIG.gapBetweenBlocks }}>
      <div style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
        <TimelineBlockTitle C={C}>{title}</TimelineBlockTitle>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {netUtilization != null && (
            <button
              onClick={onNetUtilizationPress}
              data-testid={TEST_IDS.productivityStatus.netUtilizationButton}
              style={{
                display: 'flex',
                alignItems: 'center',
                borderRadius: TIMELINE_LINE_CONFIG.borderRadius,
                border: 'none',
                padding: 0,
                backgroundColor: 'transparent',
                cursor: 'pointer',
              }}
            >
              <p style={{ ...fontStylesWeb, margin: 0, color: C(buttonContentColor) }}>
                {formatOEENumber(netUtilization * 100)}% {i18n.t('Net Utilization')}
              </p>
              <Svg icon="arrowLight" width={scale(24)} height={scale(24)} color={C('Link/Light/Primary')} transform={'translate(-2, -2)'} />
            </button>
          )}
          {onShowEventsPress && (
            <button
              onClick={onShowEventsPress}
              data-testid={TEST_IDS.productivityStatus.showEventsButton}
              style={{
                ...fontStylesWeb,
                color: C(buttonContentColor),
                borderRadius: TIMELINE_LINE_CONFIG.borderRadius,
                border: 'none',
                padding: 0,
                backgroundColor: 'transparent',
                cursor: 'pointer',
              }}
            >
              {i18n.t(!showEvents ? 'Show Events' : 'Hide Events')}
            </button>
          )}
        </div>
      </div>

      {events?.loaded ? (
        <div
          style={{
            minHeight: TIMELINE_LINE_CONFIG.height,
            width: blockWidth,
            borderRadius: TIMELINE_LINE_CONFIG.borderRadius,
            backgroundColor: C(TIMELINE_LINE_CONFIG.backgroundColor),
            display: 'flex',
            flexDirection: 'row',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {optimizedEvents?.map((i) => {
            const elementSeconds = i._optimizedPosition !== undefined ? i._optimizedPosition : localTime(i.startedOn).diff(localTime(startTime), 's');
            const elementEndSeconds = localTime(i.endedOn).diff(localTime(startTime), 's');
            const calculatedWidth = i._optimizedWidth !== undefined ? i._optimizedWidth : (elementEndSeconds - elementSeconds) * rules.secondWidth;

            // Round values to avoid sub-pixel rendering gaps
            const leftPos = Math.floor(rules.secondWidth * elementSeconds);
            // Use Math.ceil for width to ensure full pixel coverage, apply minimum display width
            const elementWidth = Math.max(Math.ceil(calculatedWidth), MIN_DISPLAY_BREAKPOINT);

            return (
              <div
                className="event"
                key={`${i.startedOn}-${i.endedOn}-event`}
                onMouseOver={(event) => {
                  const timelineRect = timelineRef?.current?.getBoundingClientRect();
                  if (timelineRect) {
                    const el: any = event.target;
                    const width = el.offsetWidth;
                    const rect = el.getBoundingClientRect();
                    const position = {
                      top: rect.top - timelineRect.top,
                      left: rect.left - timelineRect.left,
                    };

                    triggerShowPopup(
                      {
                        show: true,
                        item: { ...i, machineId: machineId ?? selectedItem?.id },
                        width,
                        position,
                      },
                      'productivityStatus'
                    );
                  }
                }}
                onMouseOut={() => {
                  triggerHidePopup('productivityStatus');
                }}
                style={{
                  position: 'absolute',
                  left: leftPos,
                  width: elementWidth,
                  minWidth: elementWidth,
                  height: '100%',
                  backgroundColor: C(machineStatusColorName[i.status]),
                }}
              />
            );
          })}
        </div>
      ) : (
        <TimelineShimmer style={{ borderRadius: TIMELINE_LINE_CONFIG.borderRadius, height: TIMELINE_LINE_CONFIG.height }} />
      )}
      {/* <div
        style={{
          display: 'flex',
          height: 20,
          width: blockWidth,
          borderRadius: 3,
          flexDirection: 'row',
          overflow: 'hidden',
        }}
      >
        {events.loaded ? (
          events.groupedByNCFile.map((i) => {
            return (
              <div
                key={`${i.startedOn}-${i.endedOn}-event-file`}
                style={{
                  width: i.durationInSeconds * rules.secondWidth,
                  minWidth: i.durationInSeconds * rules.secondWidth,
                  height: '100%',
                  overflow: 'hidden',
                  display: 'flex',
                }}
              >
                <>
                  <div
                    style={{
                      ...fontStylesWeb,
                      color: C(TIMELINE_UNDERLINE_TEXT_CONFIG.color),
                      whiteSpace: 'nowrap',
                      userSelect: 'none',
                    }}
                  >
                    {i.ncFileMain}
                  </div>
                  <div style={{ height: '100%', width: 5, background: 'Background/Light/System' }} />
                </>
              </div>
            );
          })
        ) : (
          <div />
        )}
      </div> */}
      <div
        style={{
          display: 'flex',
          height: PRODUCTIVITY_STATUS_CONFIG.ncFileImageContainerHeight + TIMELINE_UNDERLINE_TEXT_CONFIG.height,
          width: blockWidth,
          flexDirection: 'row',
          overflow: 'hidden',
        }}
      >
        {events.loaded ? (
          events.groupedByNCFile.map((i) => {
            const display = i.durationInSeconds * rules.secondWidth > 60;
            return (
              <div // we should render this element even if display false for correct positioning
                key={`${i.startedOn}-${i.endedOn}-event-file`}
                style={{
                  width: i.durationInSeconds * rules.secondWidth,
                  minWidth: i.durationInSeconds * rules.secondWidth,
                  height: '100%',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  ...(display ? { borderLeft: `1px solid ${C(TIMELINE_HAIRLINE_COLOR)}` } : {}),
                }}
              >
                <div
                  style={{
                    ...fontStylesWeb,
                    height: TIMELINE_UNDERLINE_TEXT_CONFIG.height,
                    display: 'flex',
                    alignItems: 'center',
                    color: C(TIMELINE_UNDERLINE_TEXT_CONFIG.color),
                    whiteSpace: 'nowrap',
                    userSelect: 'none',
                  }}
                >
                  {i.ncFileMain}
                </div>
                {display && <RenderNCFileImage {...{ item: i, C }} />}
              </div>
            );
          })
        ) : (
          <div />
        )}
      </div>
    </div>
  );
};
